import { BookOpenText, Power, Settings } from "lucide-react"

import LanguageBox from "@/components/language-box"
import ThemeSelector from "@/components/theme-selector"
import { GamificationLeaderboard } from "@/components/gamification/GamificationHeader"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/contexts/AuthContext"
import { useTranslation } from "react-i18next"

// 竞赛规则 PDF（取 assets/document 下首个 PDF），点「規則」打开弹窗预览
const rulesPdfModules = import.meta.glob('../assets/document/*.pdf', { eager: true, query: '?url', import: 'default' })
const rulesPdfUrl = Object.values(rulesPdfModules)[0] as string | undefined

type HeaderControlsProps = {
  className?: string
}

function RulesButton({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  if (!rulesPdfUrl) return null
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          title={t("common.rules", "規則")}
          className={
            compact
              ? "inline-flex items-center justify-center rounded-full bg-accent [html[data-theme=orange]_&]:bg-[#8FCEE0] px-3 py-1.5 text-xs font-medium text-[#1F2937] hover:brightness-95 transition"
              : "inline-flex items-center justify-center rounded-full bg-accent [html[data-theme=orange]_&]:bg-[#8FCEE0] px-4 py-2 text-sm font-medium text-[#1F2937] hover:brightness-95 transition"
          }
        >
          {t("common.rules", "規則")}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <BookOpenText className="h-5 w-5 text-primary" />
            {t("user.competition-rules-title", "競賽規則")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto rounded-lg border">
          <iframe
            src={rulesPdfUrl}
            className="w-full h-full"
            title={t("common.rules", "規則")}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default function HeaderControls({ className }: HeaderControlsProps) {
  const { t } = useTranslation()
  const { logout } = useAuth()

  return (
    <>
      {/* Inline controls — 顺序按 Figma：主题选择 / 規則 / 语言 / 登出 */}
      <div
        className={["hidden shrink-0 items-center justify-end gap-2 whitespace-nowrap min-[1180px]:flex", className]
          .filter(Boolean)
          .join(" ")}
      >
        <ThemeSelector />
        <RulesButton />
        <GamificationLeaderboard />
        <LanguageBox />
        <Button
          type="button"
          variant="ghost"
          onClick={logout}
          className="h-auto rounded-full bg-accent px-4 py-2 text-sm font-medium text-[#1F2937] hover:brightness-95 hover:bg-accent"
        >
          <Power className="mr-1" size={16} />
          {t("common.logout")}
        </Button>
      </div>

      {/* Compact controls — <1180px：菜单折叠主题/语言，外露規則/登出 */}
      <div className="flex shrink-0 items-center gap-1 min-[1180px]:hidden">
        <RulesButton compact />
        <GamificationLeaderboard compact />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <Settings className="h-5 w-5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>{t("common.theme")}</DropdownMenuLabel>
            <div className="px-2 py-1.5">
              <ThemeSelector compact />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("common.language")}</DropdownMenuLabel>
            <div className="px-2 py-1.5">
              <LanguageBox compact />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={logout}
          aria-label={t("common.logout")}
          title={t("common.logout")}
          className="h-8 w-8 shrink-0 text-gray-500 hover:bg-transparent hover:text-gray-700"
        >
          <Power className="h-5 w-5" />
        </Button>
      </div>
    </>
  )
}
